import { action, computed, flow, makeObservable, observable } from "mobx";
import { getAgentList } from "../../services/agents";
import { SUBDOMAIN_NAME_STORAGE_KEY } from "../../constants/subdomainName";
import { tokenService } from "../../services/tokenService";
import api from "../../services/api";
import { getUserByUuid } from "../../services/users";

const decodeJWT = (token) => {
    try {
        if (!token) return null;
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
        return JSON.parse(decoded);
    } catch (error) {
        console.error("Error decoding JWT:", error);
        return null;
    }
};

class AuthStore {
    isLoading = true;
    isError = false;
    isSuperAdmin = false;
    isGroupSuperAdmin = false;
    isGroupAdmin = false;
    isCustomerAdmin = false;
    isUser = false;
    users = [];
    customerId = "";
    userId = "";
    apiUserId = "";
    currentWorkspaceUuid = null;
    customer = null;
    favoriteAccount = [];
    listAccount = [];
    agents = [];
    userData = null;
    currentUserFirstName = "";
    userPermissions = [];
    navbarActionElement = null;

    constructor() {
        makeObservable(this, {
            isLoading: observable,
            isError: observable,
            isSuperAdmin: observable,
            isCustomerAdmin: observable,
            isGroupSuperAdmin: observable,
            isGroupAdmin: observable,
            isUser: observable,
            customerId: observable,
            customer: observable,
            users: observable,
            userId: observable,
            apiUserId: observable,
            currentWorkspaceUuid: observable,
            listAccount: observable,
            favoriteAccount: observable,
            agents: observable,
            userData: observable,
            currentUserFirstName: observable,
            navbarActionElement: observable,
            sortedAccountListByFavs: computed,
            clear: action,
            pickAccount: action,
            saveLocalStorageFavoriteAccounts: action,
            getGlobalInformation: flow,
            fetchAgents: flow,
            fetchUserData: flow,
            fetchUserPermissions: action,
            userPermissions: observable,
            hasPermission: action,
            setNavbarActionElement: action,
            fetchAllUsers: action,
            fetchTokenFromAuth0: flow,
            refreshTokenForWorkspace: flow,
            syncWorkspaceUuidFromToken: action,
            syncWorkspaceUuidFromWorkspace: action,
            syncRolesFromToken: action,
        });
    }

    pickAccount(id) {
        this.saveLocalStorageFavoriteAccounts(id);
        this.syncWorkspaceUuidFromWorkspace(id);
    }

    saveLocalStorageFavoriteAccounts(id) {
        const currentFavoritesAccount =
            JSON.parse(localStorage.getItem("favoriteAccount")) || [];
        const existId = currentFavoritesAccount.findIndex(
            (idSaved) => idSaved.trim() === id.trim(),
        );

        if (existId !== -1) {
            currentFavoritesAccount.splice(existId, 1);
        }

        currentFavoritesAccount.unshift(id);

        if (currentFavoritesAccount.length > 5) {
            currentFavoritesAccount.pop();
        }

        this.favoriteAccount = currentFavoritesAccount;
        localStorage.setItem(
            "favoriteAccount",
            JSON.stringify(currentFavoritesAccount),
        );
    }

    get sortedAccountListByFavs() {
        const indexMap = {};
        const currentFavoritesAccount = [...this.favoriteAccount];
        const currentAccounts = [...this.listAccount];

        currentFavoritesAccount.forEach((favorite, index) => {
            indexMap[favorite] = index;
        });

        return currentAccounts.filter(Boolean).sort((a, b) => {
            const indexA = indexMap[a?.id];
            const indexB = indexMap[b?.id];

            if (indexA !== undefined && indexB !== undefined) {
                return indexA - indexB;
            }

            if (indexA !== undefined) {
                return -1;
            }
            if (indexB !== undefined) {
                return 1;
            }

            return 0;
        });
    }

    clear() {
        localStorage.removeItem("favoriteAccount");
        localStorage.removeItem("userId");
        localStorage.removeItem("api_access_token");
        localStorage.removeItem("api_refresh_token");
        localStorage.removeItem("user_uuid");
        localStorage.removeItem("workspace_uuid");
        localStorage.removeItem(SUBDOMAIN_NAME_STORAGE_KEY);
        localStorage.removeItem("workspace_slug");
        localStorage.removeItem("tenant_slug");
        this.isLoading = false;
        this.isError = false;
        this.isSuperAdmin = false;
        this.isGroupSuperAdmin = false;
        this.isGroupAdmin = false;
        this.isCustomerAdmin = false;
        this.isUser = false;
        this.customerId = "";
        this.userId = "";
        this.apiUserId = "";
        this.currentWorkspaceUuid = null;
        this.customer = null;
        this.listAccount = [];
        this.agents = [];
        this.userData = null;
        this.currentUserFirstName = "";
        this.userPermissions = [];
    }

    /**
     * Extracts role flags from the JWT claims.
     * - user_type: global role (SUPERADMIN, USER)
     * - workspace_user_type: workspace role (OWNER, ADMIN, MEMBER, etc.)
     */
    syncRolesFromToken(token) {
        const decoded = decodeJWT(token);
        if (!decoded) return;

        const userType = (decoded.user_type || "").toUpperCase();
        const workspaceUserType = (decoded.workspace_user_type || "").toUpperCase();

        this.isSuperAdmin = userType === "SUPERADMIN";
        this.isGroupSuperAdmin = this.isSuperAdmin;

        const isOwnerOrAdmin = workspaceUserType === "OWNER" || workspaceUserType === "ADMIN";
        this.isCustomerAdmin = isOwnerOrAdmin;
        this.isGroupAdmin = isOwnerOrAdmin;

        this.isUser = workspaceUserType === "MEMBER" || userType === "USER";
    }

    *getGlobalInformation(auth0AccessToken) {
        if (!auth0AccessToken) {
            this.isLoading = false;
            return;
        }
        try {
            this.isLoading = true;
            this.userId = "auth0";

            yield this.fetchTokenFromAuth0(auth0AccessToken);
            yield this.fetchUserData();
            this.fetchUserPermissions();
            yield this.fetchAgents();
        } catch (error) {
            console.error("getGlobalInformation failed:", error);
            this.clear();
            this.isError = true;
        } finally {
            this.isLoading = false;
        }
    }

    *fetchTokenFromAuth0(auth0AccessToken) {
        const storedWorkspaceUuid = localStorage.getItem("workspace_uuid");
        const tenantUuid = storedWorkspaceUuid || process.env.REACT_APP_TENANT_UUID;

        const existingToken = tokenService.getToken();
        if (existingToken) {
            const decoded = decodeJWT(existingToken);
            const now = Math.floor(Date.now() / 1000);
            const isValid = decoded?.exp && decoded.exp > now + 60;
            const isForTargetWorkspace = !tenantUuid || decoded?.tenant_uuid === tenantUuid;

            if (isValid && isForTargetWorkspace) {
                if (decoded?.user_uuid) this.apiUserId = decoded.user_uuid;
                this.syncWorkspaceUuidFromToken(existingToken);
                this.syncRolesFromToken(existingToken);
                localStorage.setItem("user_uuid", this.apiUserId);
                return;
            }
        }

        try {
            const token = yield tokenService.fetchTokenFromAuth0(
                auth0AccessToken,
                tenantUuid,
            );

            if (token) {
                const decodedToken = decodeJWT(token);
                if (decodedToken?.user_uuid) {
                    this.apiUserId = decodedToken.user_uuid;
                }
                this.syncWorkspaceUuidFromToken(token);
                this.syncRolesFromToken(token);
                localStorage.setItem("user_uuid", this.apiUserId);
            }
        } catch (err) {
            console.error("Error fetching API token:", err);
            throw err;
        }
    }

    syncWorkspaceUuidFromToken(token) {
        const decodedToken = decodeJWT(token);
        if (decodedToken?.tenant_uuid) {
            this.currentWorkspaceUuid = decodedToken.tenant_uuid;
            localStorage.setItem("workspace_uuid", this.currentWorkspaceUuid);
        } else {
            console.warn("Could not extract tenant_uuid from token");
        }
    }

    syncWorkspaceUuidFromWorkspace(workspaceUuid) {
        if (workspaceUuid) {
            this.currentWorkspaceUuid = workspaceUuid;
            localStorage.setItem("workspace_uuid", workspaceUuid);
        }
    }

    *refreshTokenForWorkspace(tenantUuid) {
        try {
            const { data } = yield api.post("/v1/token", {
                workspace_uuid: tenantUuid,
                user_uuid: this.apiUserId,
            });

            const newToken = data?.body?.token || data?.token;
            if (!newToken) {
                const errorMsg =
                    data?.msgCode?.[0]?.description ||
                    data?.body?.msgCode?.[0]?.description ||
                    "Could not obtain token for workspace";
                throw new Error(errorMsg);
            }
            tokenService.setToken(newToken);

            const newRefreshToken = data?.body?.refresh_token || data?.refresh_token;
            tokenService.setRefreshToken(newRefreshToken);

            this.syncWorkspaceUuidFromToken(newToken);
            this.syncRolesFromToken(newToken);
        } catch (err) {
            console.error("Error refreshing token for workspace:", err);
            throw err;
        }
    }

    *fetchAgents() {
        try {
            this.isError = false;
            const response = yield getAgentList();
            const agents = response.data?.agents || [];

            this.agents = agents.map((agent) => {
                const starters =
                    Array.isArray(agent.starters) && agent.starters.length > 0
                        ? agent.starters
                        : Array.isArray(
                                agent.metadata?.conversation_starters,
                            ) && agent.metadata.conversation_starters.length > 0
                          ? agent.metadata.conversation_starters
                          : [];

                return {
                    ...agent,
                    id: agent.uuid,
                    photo_url: agent.metadata?.imageUrl || agent.image || "",
                    conversation_starters: starters,
                };
            });
        } catch (error) {
            console.error("Error fetching agents:", error);
            this.isError = true;
            this.agents = [];
        }
    }

    fetchAllUsers() {
        this.isError = false;
        try {
            this.users = [];
            return [];
        } catch (error) {
            this.isError = true;
        }
    }

    *fetchUserData() {
        if (!this.apiUserId) {
            return;
        }
        try {
            const result = yield getUserByUuid(this.apiUserId);
            const rawBody = result?.body ?? {};
            const userData = rawBody?.data ?? rawBody;
            const firstName = userData.fullName?.split(" ")[0] ?? userData.first_name ?? userData.firstName ?? "";

            this.userData = userData;
            this.currentUserFirstName = firstName;
        } catch (error) {
            console.error("Error fetching user data:", error);
            this.isError = true;
        }
    }

    *fetchUserPermissions() {
        try {
            const { data } = yield api.get("/v1/authorization/my-permissions");
            const permissions = data?.body?.permissions || data?.permissions || {};
            this.userPermissions = Object.keys(permissions);
        } catch (error) {
            console.error("Error fetching user permissions:", error);
            this.userPermissions = [];
        }
    }

    hasPermission() {
        return this.isSuperAdmin || this.isCustomerAdmin;
    }

    setNavbarActionElement(element) {
        this.navbarActionElement = element;
    }
}

export default AuthStore;
